<template>
  <div class="scorm-container">
    <iframe
      :src="`scorm/${course_id}/${executableFile}`"
      class="scorm-player"
    ></iframe>
    <transition name="fade">
      <div v-if="isLoadingProgress" class="loading-overlay">
        <div class="loading-content">
          <div class="modern-spinner">
            <div class="spinner-ring"></div>
            <div class="spinner-ring"></div>
            <div class="spinner-ring"></div>
          </div>
          <p class="loading-text">{{ loadingMessage }}</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import "scorm-again";

export default {
  props: ["course_id", "scormProgress", "executableFile"],
  data() {
    return {
      status: 0,
      _lastActivity: 0,
      idleInterval: null,
      scormPassedByAPI: false,
      isLoadingProgress: true,
      loadingTimeout: null,
      loadingMessage: "Preparing your course content",
      isSubmitting: false,
      settings: {
        autocommit: true,
        autocommitSeconds: 5,
        dataCommitFormat: "flattened",
        autoCompleteLessonStatus: true,
        logLevel: 'debug',
        lmsCommitUrl:
          process.env.VUE_APP_API_URL +
          "/employees/scorm-progress?courseId=" +
          this.course_id,
        xhrHeaders: {
          authorization: "Bearer " + localStorage.getItem("hot-token"),
          "content-type": "application/json",
        },
        responseHandler: (data) => {
          const r = JSON.parse(data.response);
          this.status = r.status;
          // Hide loading screen after first response
          if (this.isLoadingProgress) {
            this.isLoadingProgress = false;
            if (this.loadingTimeout) {
              clearTimeout(this.loadingTimeout);
              this.loadingTimeout = null;
            }
          }
          if (r.scormPassed === 1 && !this.isSubmitting) {
            this.scormPassedByAPI = true;
            this.submitEmployeeData();
          }
        },
      },
    };
  },
  created: function () {
      window.API = new Scorm12API(this.settings);

      let keyValues = [];
      this.scormProgress.forEach(data => {
          keyValues[data.key] = data.value;
      });

      window.API.cmi.suspend_data = keyValues['cmi.suspend_data'];
  },
  mounted() {
    window.addEventListener("message", this._msgHandler);

    // Fallback: hide loading screen after 15 seconds if no response
    this.loadingTimeout = setTimeout(() => {
      if (this.isLoadingProgress) {
        this.isLoadingProgress = false;
        console.warn("SCORM progress loading timeout - hiding loading screen");
      }
    }, 15000);

    if (!window.API) {
      window.API = {
        LMSInitialize: () => "true",
        LMSFinish: () => "true",
        LMSGetValue: () => "",
        LMSSetValue: () => "true",
        LMSCommit: () => "true",
        LMSGetLastError: () => "0",
        LMSGetErrorString: () => "No error",
        LMSGetDiagnostic: () => "No diagnostic",
        cmi: { core: { score: { raw: 100 } } },
      };
      console.warn(
        "⚠️ Dummy SCORM API injected for local testing. No real tracking will happen."
      );
    }

    this.ensureSDKsLoaded();
    const outer = this.$el.querySelector(".scorm-player");
    if (outer) this.watchIframeTree(outer);
  },
  beforeDestroy() {
    window.removeEventListener("message", this._msgHandler);
    if (this.idleInterval) {
      clearInterval(this.idleInterval);
      this.idleInterval = null;
    }
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
  },
  methods: {
    _msgHandler(e) {
      const { platform, action, id } = e.data;
      console.log("Parent got:", platform, action, id);

      if (action === "play" && !this.idleInterval) {
        this.idleInterval = setInterval(() => {
          window.dispatchEvent(
            new MouseEvent("mousemove", {
              bubbles: true,
              cancelable: true,
              clientX: 0,
              clientY: 0,
            })
          );
        }, 2000);
      }
      if ((action === "pause" || action === "ended") && this.idleInterval) {
        clearInterval(this.idleInterval);
        this.idleInterval = null;
      }
    },

    ensureSDKsLoaded() {
      if (!window.Vimeo) {
        const s = document.createElement("script");
        s.src = "https://player.vimeo.com/api/player.js";
        document.head.appendChild(s);
      }
      if (!window.YT || !window.YT.Player) {
        const t = document.createElement("script");
        t.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(t);
      }
    },

    processChildDocument(doc) {
      const extractEmbedly = (ifr) => {
        try {
          const raw = ifr.src || ifr.dataset.src || "";
          const u = new URL(raw);
          return u.searchParams.get("src")
            ? decodeURIComponent(u.searchParams.get("src"))
            : null;
        } catch {
          return null;
        }
      };
      const copySizing = (src, dst) => {
        dst.width = src.width || src.clientWidth || 640;
        dst.height = src.height || src.clientHeight || 360;
        dst.style.cssText = src.style.cssText;
        dst.className = src.className;
      };
      const post = (p, a, id) =>
        window.postMessage({ platform: p, action: a, id }, "*");

      // Vimeo
      const attachVimeo = (ifr) => {
        if (ifr.__vhook) return;
        ifr.__vhook = true;
        const player = new Vimeo.Player(ifr);
        const vid = ifr.src.match(/video\/(\d+)/)?.[1] || null;
        player.on("play", () => post("vimeo", "play", vid));
        player.on("pause", () => post("vimeo", "pause", vid));
        player.on("ended", () => post("vimeo", "ended", vid));
      };
      const replaceEmbedlyVimeo = (ifr) => {
        const inner = extractEmbedly(ifr);
        if (!inner?.includes("player.vimeo.com/video")) return;
        const sep = inner.includes("?") ? "&" : "?";
        const url = inner + sep + "api=1&controls=1";
        const v = doc.createElement("iframe");
        v.src = url;
        v.frameBorder = 0;
        v.allow = "autoplay; fullscreen";
        copySizing(ifr, v);
        ifr.replaceWith(v);
        attachVimeo(v);
      };

      // YouTube
      const attachYouTube = (ifr) => {
        if (ifr.__ythook) return;
        ifr.__ythook = true;
        const tryInit = () => {
          if (window.YT && YT.Player) {
            new YT.Player(ifr, {
              events: {
                onStateChange: (e) => {
                  const id =
                    ifr.src.match(/embed\/([^?\s]+)/)?.[1] || null;
                  if (e.data === YT.PlayerState.PLAYING)
                    post("youtube", "play", id);
                  if (e.data === YT.PlayerState.PAUSED)
                    post("youtube", "pause", id);
                  if (e.data === YT.PlayerState.ENDED)
                    post("youtube", "ended", id);
                },
              },
            });
          } else {
            setTimeout(tryInit, 200);
          }
        };
        tryInit();
      };
      const replaceEmbedlyYouTube = (ifr) => {
        const inner = extractEmbedly(ifr);
        if (!inner?.includes("youtube.com/embed")) return;
        const m = inner.match(/embed\/([^?\s]+)/);
        if (!m) return;
        const id = m[1];
        const p = new URLSearchParams(inner.split("?")[1] || "");
        p.set("enablejsapi", "1");
        p.set("controls", p.get("controls") || "1");
        const url = `https://www.youtube.com/embed/${id}?${p}`;
        const y = doc.createElement("iframe");
        y.src = url;
        y.frameBorder = 0;
        y.allow = "autoplay; fullscreen";
        copySizing(ifr, y);
        ifr.replaceWith(y);
        attachYouTube(y);
      };

      // Native HTML5
      const attachNative = (v) => {
        if (v.__nhook) return;
        v.__nhook = true;
        const id = v.id || null;
        v.addEventListener("play", () => post("native", "play", id));
        v.addEventListener("pause", () => post("native", "pause", id));
        v.addEventListener("ended", () => post("native", "ended", id));
      };

      // 1) handle existing
      doc.querySelectorAll("iframe.embed__media").forEach((f) => {
        replaceEmbedlyVimeo(f);
        replaceEmbedlyYouTube(f);
      });
      doc
        .querySelectorAll('iframe[src*="player.vimeo.com/video"]')
        .forEach(attachVimeo);
      doc
        .querySelectorAll('iframe[src*="youtube.com/embed"]')
        .forEach(attachYouTube);
      doc.querySelectorAll("video").forEach(attachNative);

      // Check for progress circles
      this.checkProgressCircles(doc);

      // 2) OBSERVE ANY ADDITIONS OR ATTRIBUTE CHANGES
      if (!doc.__fullObserver) {
        doc.__fullObserver = true;
        const rootObs = new MutationObserver(() => {
          // re-run your existing logic on ANY mutation
          this.processChildDocument(doc);
        });
        const target = doc.documentElement || doc.body;
        if (target) {
          rootObs.observe(target, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["src", "data-src"],
          });
        }
      }
    },

    hookUserActivity(doc) {
      const evts = [
        "pointerdown","pointermove","pointerup","pointercancel",
        "wheel","scroll","keydown","keypress","keyup",
        "click","dblclick","contextmenu",
      ];
      evts.forEach((evt) =>
        [doc, doc.body].forEach((node) =>
          node.addEventListener(evt, () => this.handleIframeActivity(), true)
        )
      );
    },

    watchIframeTree(rootIframe) {
      const attachTo = (ifr) => {
        if (ifr.__watched) return;
        ifr.__watched = true;

        const doWork = () => {
          let doc;
          try {
            doc = ifr.contentWindow.document;
          } catch {
            return;
          }
          this.processChildDocument(doc);
          this.hookUserActivity(doc);
          Array.from(doc.querySelectorAll("iframe")).forEach(attachTo);
        };

        setTimeout(doWork, 200);
        ifr.addEventListener("load", doWork);
      };

      attachTo(rootIframe);
    },

    handleIframeActivity() {
      const now = Date.now();
      if (now - this._lastActivity < 2000) return;
      this._lastActivity = now;
      window.dispatchEvent(
        new MouseEvent("mousemove", { bubbles: true, cancelable: true })
      );
    },

    checkProgressCircles(doc) {
      try {
        const progressCircles = doc.querySelectorAll(".progress-circle");
        const count = progressCircles.length;
        
        // Count how many have aria-label="Completed"
        const completedCount = Array.from(progressCircles).filter(
          (circle) => circle.getAttribute("aria-label") === "Completed"
        ).length;
        
        // Track the last known count per document
        const lastCount = doc.__lastProgressCircleCount || 0;
        
        // Log if we find new progress circles (count increased or first detection)
        if (count > 0 && count !== lastCount) {
          doc.__lastProgressCircleCount = count;
          doc.__lastCompletedCount = completedCount;
          // Only log on first detection or when count increases
          if (lastCount === 0 || count > lastCount) {
            console.log(`Found ${count} progress circle(s) in the SCORM content. ${completedCount} of them have aria-label="Completed".`);
          }
        } else if (count === 0 && lastCount > 0) {
          // Reset count if all circles are removed
          doc.__lastProgressCircleCount = 0;
          doc.__lastCompletedCount = 0;
          doc.__allCirclesCompleted = false;
        } else if (count > 0 && count === lastCount) {
          // Check if completed count changed even if total count is the same
          const lastCompletedCount = doc.__lastCompletedCount || 0;
          if (completedCount !== lastCompletedCount) {
            doc.__lastCompletedCount = completedCount;
            console.log(`Progress circles update: ${count} total, ${completedCount} with aria-label="Completed".`);
          }
        }
        
        // Check if all circles are completed and trigger submission
        // Only use this as fallback when scormPassed !== 1 from API (edge case handling)
        if (count > 0 && completedCount === count && !doc.__allCirclesCompleted && !this.scormPassedByAPI && !this.isSubmitting) {
          doc.__allCirclesCompleted = true;
          console.log(`All ${count} progress circle(s) are completed! Setting status to 1 and submitting data (fallback mechanism).`);
          this.status = 1; // Explicitly set status to 1
          this.submitEmployeeData(1); // Pass status 1 explicitly
        }
      } catch (error) {
        console.warn("Error checking for progress circles:", error);
      }
    },

    submitEmployeeData(overrideStatus = null) {
      // Prevent multiple submissions
      if (this.isSubmitting) {
        console.log("Submission already in progress, skipping duplicate call");
        return;
      }
      
      this.isSubmitting = true;
      
      // Use override status if provided (for fallback mechanism), otherwise use current status
      const statusToUse = overrideStatus !== null ? overrideStatus : this.status;
      
      // Show loading screen during submission
      this.isLoadingProgress = true;
      this.loadingMessage = "Submitting your course completion";
      
      setTimeout(() => {
        const formatted = {
          is_last_lesson: 1,
          course_id: this.course_id,
          test_id: 0,
          percentageScored: window.API.cmi
            ? window.API.cmi.core.score.raw
            : 0,
          resultStatus: statusToUse, // Use the status passed or current status
          test_type: "scorm",
          isScorm: 1,
        };
        this.$emit("save-course-completion", formatted);
        
        // Hide loading screen after submission
        setTimeout(() => {
          this.isLoadingProgress = false;
          this.isSubmitting = false; // Reset flag after submission completes
        }, 1000);
      }, 8000);
    },
  },
};
</script>

<style scoped>
.scorm-container {
  position: relative;
  width: 100%;
}

iframe.scorm-player {
  height: 80vh;
  width: 100% !important;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
}

/* Modern Spinner */
.modern-spinner {
  position: relative;
  width: 64px;
  height: 64px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.spinner-ring:nth-child(1) {
  animation-delay: -0.45s;
  border-top-color: #3b82f6;
}

.spinner-ring:nth-child(2) {
  animation-delay: -0.3s;
  border-top-color: #60a5fa;
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
}

.spinner-ring:nth-child(3) {
  animation-delay: -0.15s;
  border-top-color: #93c5fd;
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  margin: 0;
  color: #1f2937;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.025em;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>